import React, { useEffect, useState, useCallback } from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import "./CandidateJobsPopup.scss";

// ── API endpoints (mirror your InterviewPipeline constants) ──────────────────
const OPEN_JOBS_API = (candidateId) =>
  `${process.env.REACT_APP_API_HR_URL}/recruiter/recruitment/jobposts/open-jobs/applied/${candidateId}`;
const APPLY_API = `${process.env.REACT_APP_API_HR_URL}/candidate/assign/internal`; // adjust if needed
// const CANDIDATE_DETAIL_API = (candidateId) =>
//   `${process.env.REACT_APP_API_HR_URL}/candidate/applied/jobpost/all`; // same as InterviewPipeline
const HIRING_FLOW_API = `${process.env.REACT_APP_API_HR_URL}/hiring-flow`;
const EMPLOYEES_API = `${process.env.REACT_APP_API_HR_URL}/interview-history/all/employee`;
const MOVE_API = `${process.env.REACT_APP_API_HR_URL}/candidate/move-to-interview`;
const SOURCES_API = `${process.env.REACT_APP_API_HR_URL}/source/all`;

// ── Helpers (same as Interviews.jsx) ─────────────────────────────────────────
const getInitials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

const COLORS = [
  "#6366f1",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
];
const avatarColor = (seed = "") =>
  COLORS[((seed || "").charCodeAt(0) || 0) % COLORS.length];
const empName = (e) =>
  `${e.firstName || ""} ${e.lastName || ""}`.trim() || `Employee #${e.empId}`;

const getCandidateName = (c) =>
  c.candidate?.name || c.candidateName || "Unknown";
// const getCandidateEmail = (c) => c.candidate?.email || c.email || "";
const getCandidateId = (c) =>
  c.appliedCandidateId ||
  c.recuirtmentCandidateId ||
  c.appliedCandidateHistoryId;

// const getJobTitle = (c) =>
//   c.jobPostName ||
//   c.jobTitle ||
//   c.jobPost?.jobTitle ||
//   c.job?.title ||
//   c.position ||
//   "";

const getCandidateStatus = (c) => {
  const iv = (c.interviewStatus || "").toUpperCase();
  if (iv && iv !== "NOT_SCHEDULED") return iv;
  return (c.status || "APPLIED").toUpperCase();
};

const getCandidateRounds = (c) => {
  const rounds = c.interviewRounds || [];
  return rounds.map((r, i) => ({
    ...r,
    sequence: r.sequence ?? i + 1,
    initial: r.initial ?? i === 0,
    final: r.final ?? i === rounds.length - 1,
  }));
};

const STATUS_MAP = {
  APPLIED: { label: "Applied", cls: "cjp-badge--applied" },
  MOVED: { label: "Moved", cls: "cjp-badge--moved" },
  NOT_SCHEDULED: { label: "Not Scheduled", cls: "cjp-badge--applied" },
  SHORTLISTED: { label: "Shortlisted", cls: "cjp-badge--shortlisted" },
  PROCESSING: { label: "Processing", cls: "cjp-badge--processing" },
  HOLD: { label: "Hold", cls: "cjp-badge--hold" },
  REJECTED: { label: "Rejected", cls: "cjp-badge--fail" },
  SELECTED: { label: "Selected", cls: "cjp-badge--pass" },
};

const Badge = ({ status }) => {
  const s = STATUS_MAP[(status || "").toUpperCase()] || {
    label: status || "—",
    cls: "",
  };
  return <span className={`cjp-badge ${s.cls}`}>{s.label}</span>;
};

const MODES = ["ONLINE", "OFFLINE", "HYBRID"];

// ─────────────────────────────────────────────────────────────────────────────
//  Timeline Panel (inline within popup, no backdrop)
// ─────────────────────────────────────────────────────────────────────────────
const TimelinePanel = ({ candidate, flows, onClose }) => {
  if (!candidate) return null;

  //   const name = getCandidateName(candidate);
  //   const email = getCandidateEmail(candidate);
  const st = getCandidateStatus(candidate);

  const currentFlow = flows.find(
    (f) => String(f.hiringFlowId) === String(candidate.hiringFlowId),
  );
  const rawRounds =
    candidate.interviewRounds?.length > 0
      ? getCandidateRounds(candidate)
      : (currentFlow?.rounds || []).map((r, i, arr) => ({
          ...r,
          sequence: r.sequence ?? i + 1,
          initial: r.initial ?? i === 0,
          final: r.final ?? i === arr.length - 1,
        }));

  const flowRounds = rawRounds.slice().sort((a, b) => a.sequence - b.sequence);
  const currentRoundId = candidate.interviewRoundId
    ? String(candidate.interviewRoundId)
    : null;

  const getStepState = (round) => {
    const rid = String(round.interviewRoundId);
    if (!currentRoundId) return "pending";
    const rSeq = round.sequence;
    const curRound = flowRounds.find(
      (r) => String(r.interviewRoundId) === currentRoundId,
    );
    const curSeq = curRound?.sequence ?? 0;
    if (rid === currentRoundId) {
      if (["REJECTED"].includes(st)) return "rejected";
      if (["SELECTED"].includes(st)) return "selected";
      if (["HOLD"].includes(st)) return "hold";
      return "active";
    }
    if (rSeq < curSeq) return "done";
    return "pending";
  };

  const stepIcon = (state) => {
    if (state === "done")
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M2.5 7l3.5 3.5 5.5-5.5"
            stroke="#16a34a"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    if (state === "active") return <span className="cjp-tl-pulse" />;
    if (state === "rejected")
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M3 3l8 8M11 3l-8 8"
            stroke="#dc2626"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    if (state === "selected")
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M2.5 7l3.5 3.5 5.5-5.5"
            stroke="#fff"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    if (state === "hold") return <span style={{ fontSize: 11 }}>⏸</span>;
    return null;
  };

  return (
    <div className="cjp-tl-wrap">
      <div className="cjp-tl-header">
        <span className="cjp-tl-title">Hiring Progress</span>
        <button className="cjp-tl-close" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M3 3l8 8M11 3l-8 8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="cjp-tl-status-row">
        <Badge status={st} />
        {candidate.interviewRoundName && (
          <span className="cjp-tl-round-label">
            Round: <strong>{candidate.interviewRoundName}</strong>
          </span>
        )}
      </div>

      {currentFlow && (
        <div className="cjp-tl-flow-name">
          Flow: <strong>{currentFlow.hiringFlowName}</strong>
        </div>
      )}

      {flowRounds.length === 0 ? (
        <div className="cjp-tl-empty">
          {candidate.hiringFlowId
            ? "No rounds defined in this flow."
            : "No hiring flow assigned yet."}
        </div>
      ) : (
        <div className="cjp-timeline">
          {flowRounds.map((round, idx) => {
            const state = getStepState(round);
            const isLast = idx === flowRounds.length - 1;
            return (
              <div
                key={round.interviewRoundId}
                className={`cjp-step cjp-step--${state}`}
              >
                {!isLast && (
                  <div
                    className={`cjp-step__line cjp-step__line--${state === "done" ? "done" : "pending"}`}
                  />
                )}
                <div className={`cjp-step__circle cjp-step__circle--${state}`}>
                  {stepIcon(state)}
                </div>
                <div className="cjp-step__content">
                  <div className="cjp-step__row">
                    <span className="cjp-step__name">{round.roundName}</span>
                    <div className="cjp-step__tags">
                      {round.initial && (
                        <span className="cjp-tag cjp-tag--initial">
                          Initial
                        </span>
                      )}
                      {round.final && (
                        <span className="cjp-tag cjp-tag--final">Final</span>
                      )}
                    </div>
                  </div>
                  {state === "active" && (
                    <div className="cjp-step__details">
                      {candidate.interviewerName && (
                        <span className="cjp-detail">
                          👤 {candidate.interviewerName}
                        </span>
                      )}
                      {candidate.interviewDateTime && (
                        <span className="cjp-detail">
                          📅{" "}
                          {new Date(
                            candidate.interviewDateTime,
                          ).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      )}
                      {candidate.roundMode && (
                        <span className="cjp-detail">
                          {candidate.roundMode === "ONLINE"
                            ? "🌐"
                            : candidate.roundMode === "OFFLINE"
                              ? "📍"
                              : "🔀"}{" "}
                          {candidate.roundMode}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="cjp-step__seq">Step {round.sequence}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  ScheduleModal (same logic as InterviewPipeline, no status update)
// ─────────────────────────────────────────────────────────────────────────────
const ScheduleModal = ({ candidate, onClose, onDone }) => {
  const [employees, setEmployees] = useState([]);
  const [sources, setSources] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const rounds = getCandidateRounds(candidate);
  const [form, setForm] = useState({
    interviewRoundId: "",
    interviewerId: "",
    roundMode: "ONLINE",
    interviewDateTime: "",
    comments: "",
    sourceId: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const isApplied =
    getCandidateStatus(candidate) === "APPLIED" ||
    getCandidateStatus(candidate) === "NOT_SCHEDULED";

  const sourceList =
    candidate.sources?.length > 0 ? candidate.sources : sources;

  useEffect(() => {
    const calls = [
      axios
        .get(EMPLOYEES_API)
        .then((r) => r.data.data || [])
        .catch(() => []),
    ];
    if (isApplied) {
      calls.push(
        axios
          .get(SOURCES_API)
          .then((r) => r.data.data || [])
          .catch(() => []),
      );
    }
    Promise.all(calls)
      .then(([emp, src]) => {
        setEmployees(emp);
        if (isApplied && src) setSources(src);
      })
      .finally(() => setDataLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isApplied && candidate.sources?.length === 1) {
      setForm((p) => ({ ...p, sourceId: String(candidate.sources[0].id) }));
    }
  }, [candidate, isApplied]);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.interviewRoundId) e.interviewRoundId = "Select a round";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const histId = getCandidateId(candidate);
    try {
      const payload = {
        appliedCandidateHistoryId: Number(histId),
        interviewRoundId: Number(form.interviewRoundId),
        hiringFlowId: candidate.hiringFlowId,
        ...(form.interviewerId
          ? { interviewerId: Number(form.interviewerId) }
          : {}),
        roundMode: form.roundMode,
        ...(form.interviewDateTime
          ? {
              interviewDateTime: new Date(form.interviewDateTime).toISOString(),
            }
          : {}),
        comments: form.comments,
        status: "MOVED",
        ...(isApplied && form.sourceId
          ? { appliedCandidateHistorySourceId: Number(form.sourceId) }
          : {}),
      };
      const res = await axios.put(MOVE_API, [payload]);
      setResult(res.data.data || []);
      toast.success("Interview scheduled!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to schedule.");
    } finally {
      setSubmitting(false);
    }
  };

  const name = getCandidateName(candidate);

  if (result) {
    return (
      <div className="cjp-modal-overlay" onClick={onDone}>
        <div
          className="cjp-modal cjp-modal--success"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cjp-success-ring">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path
                d="M8 18l7 7 13-13"
                stroke="#16a34a"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3 className="cjp-success-title">Interview Scheduled!</h3>
          <p className="cjp-success-sub">{name} has been moved to interview.</p>
          {result.map((r, idx) => (
            <div key={idx} className="cjp-result-rows">
              {r.interviewRoundName && (
                <div className="cjp-result-row">
                  <span>Round</span>
                  <strong>{r.interviewRoundName}</strong>
                </div>
              )}
              {r.interviewerName && (
                <div className="cjp-result-row">
                  <span>Interviewer</span>
                  <strong>{r.interviewerName}</strong>
                </div>
              )}
              {r.status && (
                <div className="cjp-result-row">
                  <span>Status</span>
                  <Badge status={r.status} />
                </div>
              )}
            </div>
          ))}
          <button className="cjp-done-btn" onClick={onDone}>
            Done ✓
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cjp-modal-overlay" onClick={onClose}>
      <div className="cjp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cjp-modal__hd">
          <div>
            <h3 className="cjp-modal__title">Schedule Interview</h3>
            <p className="cjp-modal__sub">
              Move <strong>{name}</strong> to an interview round
            </p>
          </div>
          <button className="cjp-modal__x" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M4 4l10 10M14 4L4 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {dataLoading ? (
          <div className="cjp-loading-state">
            <span className="cjp-spin" /> Loading data...
          </div>
        ) : (
          <div className="cjp-form-body">
            {rounds.length > 0 ? (
              <div className="cjp-field">
                <label className="cjp-lbl">
                  Interview Round <span className="cjp-req">*</span>
                </label>
                <div className="cjp-rounds">
                  {rounds.map((r, idx) => {
                    const active =
                      String(form.interviewRoundId) ===
                      String(r.interviewRoundId);
                    return (
                      <button
                        key={r.interviewRoundId || idx}
                        type="button"
                        className={`cjp-round-pill${active ? " cjp-round-pill--on" : ""}`}
                        onClick={() =>
                          set("interviewRoundId", String(r.interviewRoundId))
                        }
                      >
                        <span className="cjp-round-pill__seq">
                          {r.sequence}
                        </span>
                        <span className="cjp-round-pill__txt">
                          {r.roundName}
                        </span>
                        {r.initial && (
                          <span className="cjp-round-pill__tag">Initial</span>
                        )}
                        {r.final && (
                          <span className="cjp-round-pill__tag cjp-round-pill__tag--f">
                            Final
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {errors.interviewRoundId && (
                  <span className="cjp-err">{errors.interviewRoundId}</span>
                )}
              </div>
            ) : (
              <div className="cjp-no-rounds">
                No interview rounds found for this job post.
              </div>
            )}

            <div className="cjp-field">
              <label className="cjp-lbl">Interview Mode</label>
              <div className="cjp-mode-row">
                {MODES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`cjp-mode-pill${form.roundMode === m ? " cjp-mode-pill--on" : ""}`}
                    onClick={() => set("roundMode", m)}
                  >
                    {m === "ONLINE" ? "🌐" : m === "OFFLINE" ? "📍" : "🔀"} {m}
                  </button>
                ))}
              </div>
            </div>

            <div className="cjp-two-col">
              <div className="cjp-field">
                <label className="cjp-lbl">
                  Interviewer{" "}
                  <span className="cjp-optional-lbl">(optional)</span>
                </label>
                <select
                  className="cjp-sel"
                  value={form.interviewerId}
                  onChange={(e) => set("interviewerId", e.target.value)}
                >
                  <option value="">Select interviewer</option>
                  {employees.map((e) => (
                    <option key={e.empId} value={e.empId}>
                      {empName(e)}
                      {e.companyEmail ? ` — ${e.companyEmail}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="cjp-field">
                <label className="cjp-lbl">
                  Date &amp; Time{" "}
                  <span className="cjp-optional-lbl">(optional)</span>
                </label>
                <input
                  className="cjp-inp"
                  type="datetime-local"
                  value={form.interviewDateTime}
                  min={new Date().toISOString().slice(0, 16)}
                  onChange={(e) => set("interviewDateTime", e.target.value)}
                />
              </div>
            </div>

            {isApplied && sourceList.length > 0 && (
              <div className="cjp-field">
                <label className="cjp-lbl">
                  Source <span className="cjp-optional-lbl">(optional)</span>
                </label>
                <select
                  className="cjp-sel"
                  value={form.sourceId}
                  onChange={(e) => set("sourceId", e.target.value)}
                  disabled={sourceList.length === 1}
                >
                  <option value="">Select source</option>
                  {sourceList.map((s) => (
                    <option key={s.id || s.sourceId} value={s.id || s.sourceId}>
                      {s.sourceName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="cjp-field">
              <label className="cjp-lbl">Comments / Instructions</label>
              <textarea
                className="cjp-ta"
                rows={3}
                placeholder="Topics to assess, any special instructions..."
                value={form.comments}
                onChange={(e) => set("comments", e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="cjp-modal__ft">
          <button className="cjp-ghost-btn" onClick={onClose}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="cjp-primary-btn"
            onClick={handleSubmit}
            disabled={submitting || dataLoading}
          >
            {submitting ? (
              <>
                <span className="cjp-spin cjp-spin--lt" /> Scheduling...
              </>
            ) : (
              "Schedule Interview →"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  ApplyConfirmModal — confirm applying to a new job
// ─────────────────────────────────────────────────────────────────────────────
const ApplyConfirmModal = ({
  job,
  candidateId,
  candidateName,
  onClose,
  onDone,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const handleApply = async () => {
    setSubmitting(true);
    try {
      await axios.post(APPLY_API, {
        candidateId: Number(candidateId),
        jobPostId: Number(job.jobPostId),
      });
      toast.success(
        `${candidateName} applied to "${job.jobTitle}" successfully!`,
      );
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to apply.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cjp-modal-overlay" onClick={onClose}>
      <div
        className="cjp-modal cjp-modal--confirm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cjp-confirm-icon">💼</div>
        <h3 className="cjp-confirm-title">Apply to Job</h3>
        <p className="cjp-confirm-body">
          Apply <strong>{candidateName}</strong> to{" "}
          <strong>{job.jobTitle}</strong>?
        </p>
        <div className="cjp-confirm-actions">
          <button className="cjp-ghost-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="cjp-primary-btn"
            onClick={handleApply}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="cjp-spin cjp-spin--lt" /> Applying...
              </>
            ) : (
              "Yes, Apply →"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  JobCard — single job row inside the popup
// ─────────────────────────────────────────────────────────────────────────────
const JobCard = ({
  job,
  appliedCandidateData,
  flows,
  onSchedule,
  onViewTimeline,
  onApply,
}) => {
  const isApplied = job.applied;
  const candidateForJob = appliedCandidateData; // enriched pipeline data for this job

  const st = candidateForJob ? getCandidateStatus(candidateForJob) : null;
  const isAppliedStatus = st === "APPLIED" || st === "NOT_SCHEDULED";
  //   const isScheduled =
  //     candidateForJob &&
  //     (!!candidateForJob.interviewHistoryId ||
  //       candidateForJob.scheduledInterview === true ||
  //       st === "MOVED");

  return (
    <div className={`cjp-job-card${isApplied ? " cjp-job-card--applied" : ""}`}>
      <div className="cjp-job-card__top">
        <div className="cjp-job-card__info">
          <span className="cjp-job-card__title">{job.jobTitle}</span>
          <div className="cjp-job-card__meta">
            <span
              className={`cjp-job-status-pill cjp-job-status-pill--${(job.status || "").toLowerCase()}`}
            >
              {job.status}
            </span>
            {isApplied && st && <Badge status={st} />}
          </div>
        </div>

        <div className="cjp-job-card__actions">
          {isApplied ? (
            <>
              {candidateForJob && (
                <button
                  className="cjp-action-btn cjp-action-btn--timeline"
                  onClick={() => onViewTimeline(candidateForJob)}
                >
                  📊 Timeline
                </button>
              )}
              {candidateForJob && isAppliedStatus && (
                <button
                  className="cjp-action-btn cjp-action-btn--schedule"
                  onClick={() => onSchedule(candidateForJob)}
                >
                  📅 Schedule
                </button>
              )}
              {/* Update Status button intentionally hidden per requirements */}
              <span className="cjp-applied-badge">✓ Applied</span>
            </>
          ) : (
            <button
              className="cjp-action-btn cjp-action-btn--apply"
              onClick={() => onApply(job)}
            >
              + Apply
            </button>
          )}
        </div>
      </div>

      {/* Interview round info if scheduled */}
      {candidateForJob?.interviewRoundName && (
        <div className="cjp-job-card__round-info">
          <span>📋 {candidateForJob.interviewRoundName}</span>
          {candidateForJob.interviewerName && (
            <span>· 👤 {candidateForJob.interviewerName}</span>
          )}
          {candidateForJob.interviewDateTime && (
            <span>
              · 📅{" "}
              {new Date(candidateForJob.interviewDateTime).toLocaleDateString(
                "en-IN",
                { day: "2-digit", month: "short", year: "numeric" },
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  CandidateJobsPopup — main export
// ─────────────────────────────────────────────────────────────────────────────
const CandidateJobsPopup = ({ candidate, onClose }) => {
  const [jobs, setJobs] = useState([]);
  const [pipelineData, setPipelineData] = useState([]); // full interview pipeline data
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [timelineCandidate, setTimelineCandidate] = useState(null);
  const [applyTarget, setApplyTarget] = useState(null);

  const candidateId = candidate.recuirtmentCandidateId;
  const candidateName = candidate.candidateName || "Candidate";

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios
        .get(OPEN_JOBS_API(candidateId))
        .then((r) => r.data.data || [])
        .catch(() => []),
      axios
        .get(
          `${process.env.REACT_APP_API_HR_URL}/candidate/applied/jobpost/all`,
        )
        .then((r) => r.data.data || [])
        .catch(() => []),
      axios
        .get(HIRING_FLOW_API)
        .then((r) => r.data.data || [])
        .catch(() => []),
    ])
      .then(([openJobs, pipeline, hf]) => {
        setJobs(openJobs);
        // Filter pipeline data to this candidate only
        const candidatePipeline = pipeline.filter((p) => {
          const pid =
            p.recuirtmentCandidateId || p.candidate?.recuirtmentCandidateId;
          return String(pid) === String(candidateId);
        });
        setPipelineData(candidatePipeline);
        setFlows(hf);
      })
      .finally(() => setLoading(false));
  }, [candidateId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPipelineDataForJob = (jobPostId) =>
    pipelineData.find((p) => {
      const jid = p.jobPostId || p.jobPost?.jobPostId;
      return String(jid) === String(jobPostId);
    }) || null;

  const appliedCount = jobs.filter((j) => j.applied).length;
  const notAppliedCount = jobs.filter((j) => !j.applied).length;

  const handleScheduleDone = () => {
    setScheduleTarget(null);
    fetchData();
  };

  const handleApplyDone = () => {
    setApplyTarget(null);
    fetchData();
  };

  return (
    <>
      {/* Main popup */}
      <div className="cjp-backdrop" onClick={onClose}>
        <div className="cjp-popup" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="cjp-popup__hd">
            <div className="cjp-popup__cand">
              <div
                className="cjp-avatar"
                style={{ background: avatarColor(candidateName) }}
              >
                {getInitials(candidateName)}
              </div>
              <div>
                <div className="cjp-popup__name">{candidateName}</div>
                <div className="cjp-popup__email">{candidate.email || "—"}</div>
              </div>
            </div>
            <button className="cjp-popup__x" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M4 4l10 10M14 4L4 14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Stats strip */}
          {!loading && (
            <div className="cjp-stats-strip">
              <div className="cjp-stat">
                <span className="cjp-stat__num">{jobs.length}</span>
                <span className="cjp-stat__label">Open Jobs</span>
              </div>
              <div className="cjp-stat">
                <span className="cjp-stat__num cjp-stat__num--applied">
                  {appliedCount}
                </span>
                <span className="cjp-stat__label">Applied</span>
              </div>
              <div className="cjp-stat">
                <span className="cjp-stat__num cjp-stat__num--open">
                  {notAppliedCount}
                </span>
                <span className="cjp-stat__label">Available</span>
              </div>
            </div>
          )}

          {/* Body */}
          <div className="cjp-popup__body">
            {loading ? (
              <div className="cjp-loading-state">
                <span className="cjp-spin" /> Loading jobs...
              </div>
            ) : jobs.length === 0 ? (
              <div className="cjp-empty">
                <span>📋</span>
                <p>No open job posts found.</p>
              </div>
            ) : (
              <>
                {/* Applied jobs section */}
                {appliedCount > 0 && (
                  <div className="cjp-section">
                    <div className="cjp-section__title">
                      <span className="cjp-section__dot cjp-section__dot--applied" />
                      Applied Jobs
                      <span className="cjp-section__count">{appliedCount}</span>
                    </div>
                    <div className="cjp-jobs-list">
                      {jobs
                        .filter((j) => j.applied)
                        .map((job) => {
                          const pipeData = getPipelineDataForJob(job.jobPostId);
                          return (
                            <JobCard
                              key={job.jobPostId}
                              job={job}
                              appliedCandidateData={pipeData}
                              flows={flows}
                              onSchedule={setScheduleTarget}
                              onViewTimeline={setTimelineCandidate}
                              onApply={setApplyTarget}
                            />
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Available jobs section */}
                {notAppliedCount > 0 && (
                  <div className="cjp-section">
                    <div className="cjp-section__title">
                      <span className="cjp-section__dot cjp-section__dot--open" />
                      Available to Apply
                      <span className="cjp-section__count">
                        {notAppliedCount}
                      </span>
                    </div>
                    <div className="cjp-jobs-list">
                      {jobs
                        .filter((j) => !j.applied)
                        .map((job) => (
                          <JobCard
                            key={job.jobPostId}
                            job={job}
                            appliedCandidateData={null}
                            flows={flows}
                            onSchedule={setScheduleTarget}
                            onViewTimeline={setTimelineCandidate}
                            onApply={setApplyTarget}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Inline timeline panel */}
            {timelineCandidate && (
              <TimelinePanel
                candidate={timelineCandidate}
                flows={flows}
                onClose={() => setTimelineCandidate(null)}
              />
            )}
          </div>

          <div className="cjp-popup__ft">
            <button className="cjp-ghost-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Schedule modal (outside popup so it stacks properly) */}
      {scheduleTarget && (
        <ScheduleModal
          candidate={scheduleTarget}
          onClose={() => setScheduleTarget(null)}
          onDone={handleScheduleDone}
        />
      )}

      {/* Apply confirm modal */}
      {applyTarget && (
        <ApplyConfirmModal
          job={applyTarget}
          candidateId={candidateId}
          candidateName={candidateName}
          onClose={() => setApplyTarget(null)}
          onDone={handleApplyDone}
        />
      )}
    </>
  );
};

export default CandidateJobsPopup;
